import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css'; // RE-ADDED: Ensure Toastify CSS is imported here for visibility
import { BACKEND_URL } from '../Constants';

// IMPORTANT: Ensure Bootstrap 5 CSS is loaded in your project's index.html or App.js
// Example: <link href="https://cdn.jsdelivr.net/npm/bootstrap@5.3.0/dist/css/bootstrap.min.css" rel="stylesheet">
// And Bootstrap JS if you need its interactive components (though modals here are custom React)
// Example: <script src="https://cdn.jsdelivr.net/npm/bootstrap@5.3.0/dist/js/bootstrap.bundle.min.js"></script>
// Also ensure 'react-toastify/dist/ReactToastify.css' is imported globally if needed (though now it's imported directly here).

const StudentsList = () => {
    const [students, setStudents] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    // State for the Add Student Modal
    const [isAddStudentModalOpen, setIsAddStudentModalOpen] = useState(false);
    const [newStudentEmail, setNewStudentEmail] = useState('');
    const [newStudentPassword, setNewStudentPassword] = useState('');
    const [newStudentRole, setNewStudentRole] = useState('student'); // Default role to 'student'
    const [newStudentName, setNewStudentName] = useState('');
    const [showNewPassword, setShowNewPassword] = useState(false);

    // State for the Edit Student Modal
    const [isEditStudentModalOpen, setIsEditStudentModalOpen] = useState(false);
    const [editingStudent, setEditingStudent] = useState(null); // Stores the student object being edited
    const [editStudentName, setEditStudentName] = useState('');
    const [editStudentEmail, setEditStudentEmail] = useState('');
    const [editStudentRole, setEditStudentRole] = useState('');
    const [editStudentPassword, setEditStudentPassword] = useState(''); // NEW: State for editing password
    const [showEditPassword, setShowEditPassword] = useState(false);

    // Function to fetch students (re-usable)
    const fetchStudents = async () => {
        setLoading(true);
        setError(null);
        try {
            const response = await axios.get(`${ BACKEND_URL }/api/users/students`);
            setStudents(response.data);
        } catch (err) {
            console.error('Error fetching students:', err);
            setError('Failed to load students. Please check the server connection.');
            toast.error('Failed to load students.');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchStudents();
    }, []); // Empty dependency array means this runs once on component mount

    // Handle adding a new student
    const handleAddStudent = async (e) => {
        e.preventDefault(); // Prevent default form submission

        if (!newStudentName || !newStudentEmail || !newStudentPassword) {
            toast.error('Please fill in all fields.');
            return;
        }

        try {
            const response = await axios.post(`${ BACKEND_URL }/api/auth/register`, {
                name: newStudentName,
                email: newStudentEmail,
                password: newStudentPassword,
                role: newStudentRole
            });

            setIsAddStudentModalOpen(false);
            toast.success('Student added successfully!');
            setTimeout(fetchStudents, 100); // Add small delay
            // Clear form fields
            setNewStudentName('');
            setNewStudentEmail('');
            setNewStudentPassword('');
            setNewStudentRole('student'); // Reset to default
        } catch (err) {
            console.error('Error adding student:', err);
            const errorMessage = err.response?.data?.message || 'Failed to add student. Please try again.';
            toast.error(errorMessage);
        }
    };

    // Open Edit Student Modal
    const openEditStudentModal = (student) => {
        setEditingStudent(student);
        setEditStudentName(student.name || '');
        setEditStudentEmail(student.email || '');
        setEditStudentRole(student.role || 'student');
        setEditStudentPassword(''); // NEW: Clear password field when opening modal for security
        setShowEditPassword(false);
        setIsEditStudentModalOpen(true);
    };

    // Handle updating a student
    const handleUpdateStudent = async (e) => {
        e.preventDefault();

        if (!editStudentName || !editStudentEmail || !editingStudent._id) {
            toast.error('Name and Email are required.');
            return;
        }

        const updateData = {
            name: editStudentName,
            email: editStudentEmail,
            role: editStudentRole
        };

        // NEW: Only add password to update data if it's not empty
        if (editStudentPassword) {
            updateData.password = editStudentPassword;
        }

        try {
            const response = await axios.put(`${ BACKEND_URL }/api/users/${editingStudent._id}`, updateData);

            setIsEditStudentModalOpen(false); // Changed from setIsEditModalOpen to setIsEditStudentModalOpen
            toast.success('Student updated successfully!');
            setTimeout(fetchStudents, 300); // Add small delay
            // MODIFIED: Corrected modal close state variable
            setEditingStudent(null);
            setEditStudentPassword(''); // NEW: Clear password state after successful update
            setShowEditPassword(false);
        } catch (err) {
            console.error('Error updating student:', err);
            const errorMessage = err.response?.data?.message || 'Failed to update student. Please try again.';
            toast.error(errorMessage);
        }
    };

    // Handle deleting a student
    const handleDeleteStudent = async (studentId) => {
        // IMPORTANT: window.confirm should be replaced with a custom modal for better UX in a React app.
        if (!window.confirm('Are you sure you want to delete this student? This action cannot be undone.')) {
            return;
        }

        try {
            await axios.delete(`${ BACKEND_URL }/api/users/${studentId}`);
            toast.success('Student deleted successfully!');
            setTimeout(fetchStudents, 100); // Add small delay
        } catch (err) {
            console.error('Error deleting student:', err);
            const errorMessage = err.response?.data?.message || 'Failed to delete student. Please try again.';
            toast.error(errorMessage);
        }
    };

/*
    if (loading) {
        return (
            <div className="d-flex justify-content-center align-items-center vh-100">
                <p className="text-muted fs-5">Loading students...</p>
            </div>
        );
    }
*/
    if (error) {
        return (
            <div className="d-flex justify-content-center align-items-center vh-100">
                <div className="alert alert-danger text-center" role="alert">
                    <strong className="font-weight-bold">Error!</strong>
                    <span className="d-block d-sm-inline"> {error}</span>
                </div>
            </div>
        );
    }
/*
    if (!students || students.length === 0) {
        return (
            <div className="d-flex flex-column justify-content-center align-items-center vh-100">
                <p className="text-muted fs-5">No students found.</p>
                <button
                    onClick={() => setIsAddStudentModalOpen(true)}
                    className="btn btn-primary mt-3 shadow-sm"
                >
                    Add New Student
                </button>
            </div>
        );
    }
*/
    return (
        <div className="container py-4">
            <div className="d-flex justify-content-between align-items-center mb-4">
                <h2 className="text-dark mb-0">All Students</h2>
                <button
                    onClick={() => setIsAddStudentModalOpen(true)}
                    className="btn btn-primary shadow-sm"
                >
                    Add New Student
                </button>
            </div>

            <div className="card shadow-sm">
                <div className="card-body p-0"> {/* p-0 to remove default card padding for table */}
                    <div className="table-responsive">
                        {/* MODIFIED: Ensure no whitespace between <table> and <thead>/<tbody> */}
                        <table className="table table-hover mb-0">
                            <thead>
                                <tr>
                                    <th scope="col" className="text-start text-uppercase text-muted small">
                                        #
                                    </th>
                                    <th scope="col" className="text-start text-uppercase text-muted small">
                                        Name
                                    </th>
                                    <th scope="col" className="text-start text-uppercase text-muted small">
                                        Email
                                    </th>
                                    <th scope="col" className="text-start text-uppercase text-muted small">
                                        Role
                                    </th>
                                    <th scope="col" className="text-start text-uppercase text-muted small">
                                        Actions
                                    </th>
                                </tr>
                            </thead>
                            <tbody>
                                {students.map((student, index) => (
                                    <tr key={student._id}>
                                        <td className="align-middle">
                                            {index + 1}
                                        </td>
                                        <td className="align-middle">
                                            {student.name || 'N/A'}
                                        </td>
                                        <td className="align-middle">
                                            {student.email}
                                        </td>
                                        <td className="align-middle text-capitalize">
                                            {student.role}
                                        </td>
                                        <td className="align-middle">
                                            <button
                                                onClick={() => openEditStudentModal(student)}
                                                className="btn btn-sm btn-info me-2" // Bootstrap info button, margin-right
                                            >
                                                Edit
                                            </button>
                                            <button
                                                onClick={() => handleDeleteStudent(student._id)}
                                                className="btn btn-sm btn-danger" // Bootstrap danger button
                                            >
                                                Delete
                                            </button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>

            {/* Add Student Modal */}
            {isAddStudentModalOpen && (
                <div className="modal d-block" tabIndex="-1" role="dialog" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
                    <div className="modal-dialog modal-dialog-centered" role="document">
                        <div className="modal-content rounded-lg shadow-lg p-3">
                            <div className="modal-header">
                                <h5 className="modal-title">Add New Student</h5>
                                <button
                                    type="button"
                                    className="btn-close"
                                    aria-label="Close"
                                    onClick={() => setIsAddStudentModalOpen(false)}
                                ></button>
                            </div>
                            <form onSubmit={handleAddStudent}>
                                <div className="modal-body">
                                    <div className="mb-3">
                                        <label htmlFor="newStudentName" className="form-label">
                                            Name:
                                        </label>
                                        <input
                                            type="text"
                                            id="newStudentName"
                                            className="form-control rounded"
                                            value={newStudentName}
                                            onChange={(e) => setNewStudentName(e.target.value)}
                                            required
                                        />
                                    </div>
                                    <div className="mb-3">
                                        <label htmlFor="newStudentEmail" className="form-label">
                                            Email:
                                        </label>
                                        <input
                                            type="email"
                                            id="newStudentEmail"
                                            className="form-control rounded"
                                            value={newStudentEmail}
                                            onChange={(e) => setNewStudentEmail(e.target.value)}
                                            required
                                        />
                                    </div>
                                    <div className="mb-3">
                                        <label htmlFor="newStudentPassword" className="form-label">
                                            Password:
                                        </label>
                                        <div className="input-group">
                                            <input
                                                type={showNewPassword ? 'text' : 'password'}
                                                id="newStudentPassword"
                                                className="form-control rounded-start"
                                                value={newStudentPassword}
                                                onChange={(e) => setNewStudentPassword(e.target.value)}
                                                required
                                            />
                                            <button
                                                type="button"
                                                className="btn btn-outline-secondary rounded-end"
                                                onClick={() => setShowNewPassword(!showNewPassword)}
                                            >
                                                <i className={showNewPassword ? 'fa fa-eye-slash' : 'fa fa-eye'}></i>
                                            </button>
                                        </div>
                                    </div>
                                </div>
                                <div className="modal-footer">
                                    <button
                                        type="button"
                                        className="btn btn-secondary rounded"
                                        onClick={() => setIsAddStudentModalOpen(false)}
                                    >
                                        Cancel
                                    </button>
                                    <button
                                        type="submit"
                                        className="btn btn-primary rounded"
                                    >
                                        Add Student
                                    </button>
                                </div>
                            </form>
                        </div>
                    </div>
                </div>
            )}

            {/* Edit Student Modal */}
            {isEditStudentModalOpen && editingStudent && (
                <div className="modal d-block" tabIndex="-1" role="dialog" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
                    <div className="modal-dialog modal-dialog-centered" role="document">
                        <div className="modal-content rounded-lg shadow-lg p-3">
                            <div className="modal-header">
                                <h5 className="modal-title">Edit Student</h5>
                                <button
                                    type="button"
                                    className="btn-close"
                                    aria-label="Close"
                                    onClick={() => setIsEditStudentModalOpen(false)}
                                ></button>
                            </div>
                            <form onSubmit={handleUpdateStudent}>
                                <div className="modal-body">
                                    <div className="mb-3">
                                        <label htmlFor="editStudentName" className="form-label">
                                            Name:
                                        </label>
                                        <input
                                            type="text"
                                            id="editStudentName"
                                            className="form-control rounded"
                                            value={editStudentName}
                                            onChange={(e) => setEditStudentName(e.target.value)}
                                            required
                                        />
                                    </div>
                                    <div className="mb-3">
                                        <label htmlFor="editStudentEmail" className="form-label">
                                            Email:
                                        </label>
                                        <input
                                            type="email"
                                            id="editStudentEmail"
                                            className="form-control rounded"
                                            value={editStudentEmail}
                                            onChange={(e) => setEditStudentEmail(e.target.value)}
                                            required
                                        />
                                    </div>
                                   {/* NEW: Password field for editing */}
                                   <div className="mb-3">
                                        <label htmlFor="editStudentPassword" className="form-label">
                                            New Password (leave blank to keep current):
                                        </label>
                                        <div className="input-group">
                                            <input
                                                type={showEditPassword ? 'text' : 'password'}
                                                id="editStudentPassword"
                                                className="form-control rounded-start"
                                                value={editStudentPassword}
                                                onChange={(e) => setEditStudentPassword(e.target.value)}
                                                // Optional: Add a minLength for password
                                                // minLength="6"
                                            />
                                            <button
                                                type="button"
                                                className="btn btn-outline-secondary rounded-end"
                                                onClick={() => setShowEditPassword(!showEditPassword)}
                                            >
                                                <i className={showEditPassword ? 'fa fa-eye-slash' : 'fa fa-eye'}></i>
                                            </button>
                                        </div>
                                    </div>
                                    <div className="mb-3" style={{ 'display': 'none' }}>
                                        <label htmlFor="editStudentRole" className="form-label">
                                            Role:
                                        </label>
                                        <select
                                            id="editStudentRole"
                                            className="form-select rounded"
                                            value={editStudentRole}
                                            onChange={(e) => setEditStudentRole(e.target.value)}
                                        >
                                            <option value="student">Student</option>
                                            <option value="instructor">Instructor</option>
                                            <option value="admin">Admin</option>
                                        </select>
                                    </div>
                                </div>
                                <div className="modal-footer">
                                    <button
                                        type="button"
                                        className="btn btn-secondary rounded"
                                        onClick={() => setIsEditStudentModalOpen(false)}
                                    >
                                        Cancel
                                    </button>
                                    <button
                                        type="submit"
                                        className="btn btn-primary rounded"
                                    >
                                        Save Changes
                                    </button>
                                </div>
                            </form>
                        </div>
                    </div>
                </div>
            )}
            <ToastContainer position="top-right" autoClose={2000} />
        </div>
    );
};

export default StudentsList;
